/**
 * Modal que exibe as previsões de vendas de um produto
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Close, WhatsApp } from '@mui/icons-material';
import { ProdutoPrevisaoVendas } from '@types';
import { WhatsAppUtil } from '@utils';

interface ModalPrevisaoVendasProps {
  open: boolean;
  onClose: () => void;
  produtoCodigo: string;
  produtoNome: string;
  previsoes: ProdutoPrevisaoVendas[];
  isLoading: boolean;
}

export const ModalPrevisaoVendas: React.FC<ModalPrevisaoVendasProps> = ({
  open,
  onClose,
  produtoCodigo,
  produtoNome,
  previsoes,
  isLoading,
}) => {
  const handleWhatsAppClick = (previsao: ProdutoPrevisaoVendas) => {
    WhatsAppUtil.solicitarEstoque({
      vendedorNome: previsao.vendedorNome,
      produtoCodigo,
      produtoNome,
      quantidadeDisponivel: parseInt(previsao.saldo) || 0,
    });
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Previsões de Vendas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {produtoNome}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : previsoes.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <Typography color="text.secondary">
              Nenhuma previsão encontrada para este produto
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>
                    <strong>Nome</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Quantidade</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Saldo</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>Porcentagem</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>
                    <strong>Ações</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previsoes.map((previsao, index) => {
                  const percentual = parseFloat(previsao.percentual);
                  const cor = percentual >= 75 ? 'success.main' : percentual >= 50 ? 'warning.main' : 'error.main';

                  return (
                    <TableRow
                      key={`${previsao.vendedor}-${index}`}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <TableCell>{previsao.vendedorNome}</TableCell>
                      <TableCell align="right">{previsao.quantidade}</TableCell>
                      <TableCell align="right">{previsao.saldo}</TableCell>
                      <TableCell align="right">
                        <Typography
                          component="span"
                          sx={{
                            color: cor,
                            fontWeight: 600,
                          }}
                        >
                          {percentual.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Solicitar estoque via WhatsApp" arrow>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleWhatsAppClick(previsao)}
                            disabled={parseInt(previsao.saldo) <= 0}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'success.light',
                                color: 'white',
                              },
                            }}
                          >
                            <WhatsApp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};
